import React from "react";
import { PropTypes } from "prop-types";
import { useViewport } from "@bca/ui-library";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../Button/Button";
import {
  StyledModal,
  StyledModalContent,
  ModalTitle,
  ModalSubtitle,
  StyledNewSelect,
} from "./SocialSelectModal.styled";
import { getSocialSelectModal } from "../../reducers/socialSelectModalSlice";
import { changeFoundInterest, resetData } from "../../reducers/formSlice";
import { changeShowLoadingScreenState } from "../../reducers/loadingScreenSlice";
import { setError } from "../../reducers/thankYouSlice";
import {
  setPageError,
  setShowServerErrorMessage,
} from "../../reducers/errorManagementSlice";
import { setSfLeadId } from "../../reducers/salesforceLeadIdSlice";

const SocialSelectModal = ({ showModal, modalHandler }) => {
  const { page_content, loading } = useSelector(
    (state) => state.socialSelectModal
  );
  const { country_name: countrySelected, ...formState } = useSelector(
    (state) => state.form
  );

  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const vp = useViewport();
  const isMobile = vp.lessThan.md;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const submitForm = async () => {
    const baseUrl = process.env.REACT_APP_API;

    if (window.digitalData) {
      window.digitalData.push({
        event: {
          name: "form-submit",
          category: "form",
          label: "new-registration",
          action: "submit",
        },
      });
      window.digitalData.push({
        event: {
          name: "click",
          category: "dropdown",
          label: `where-did-you-hear|${formState?.found_interest?.value}`,
          action: "select",
          section: "pop-up",
        },
      });
    }

    dispatch(changeShowLoadingScreenState(true));

    const data = await fetch(
      `${baseUrl}/api/submit-registration/${countrySelected}/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
          "Accept-Language": languageCode === "en" ? "en-UK" : languageCode,
        },
        body: JSON.stringify({ ...formState, country_iso3 }),
      }
    );
    if (data.status === 400) {
      dispatch(setPageError({ page: "submit_registration", status: 400 }));
    }
    dispatch(setShowServerErrorMessage(data.status));

    if (data.ok) {
      const response = await data.json();
      dispatch(setSfLeadId(response?.sf_lead_id));
      dispatch(changeShowLoadingScreenState(false));
      dispatch(setError(response.error));
      dispatch(resetData(countrySelected));
      navigate("/thank-you");
    }
  };

  React.useEffect(() => {
    dispatch(getSocialSelectModal(countrySelected, languageCode));
  }, [dispatch, countrySelected, languageCode]);

  React.useEffect(() => {
    if (window.digitalData && showModal) {
      window.digitalData.push({
        event: {
          name: "click",
          category: "pop-up",
          label: "heard-about-bca",
          action: "open",
        },
      });
    }
  }, [showModal]);

  return (
    showModal &&
    !loading && (
      <StyledModal
        showXButton={false}
        onClose={() => modalHandler()}
        isMobile={isMobile}
      >
        <StyledModalContent>
          <ModalTitle>{page_content?.header}</ModalTitle>
          <ModalSubtitle>
            {page_content?.where_did_you_hear_about_bca_label}
          </ModalSubtitle>
          <StyledNewSelect
            data-cy="social-select-modal-select"
            label={page_content?.select_options?.label}
            message={
              page_content?.where_did_you_hear_about_bca_required_message
            }
            isMobile={isMobile}
            required={page_content?.select_options?.required}
            onChange={(event) =>
              dispatch(changeFoundInterest(event.target.value))
            }
            value={formState?.found_interest?.value}
          >
            {page_content?.select_options?.options?.map((option) => {
              return (
                <StyledNewSelect.Option
                  key={option?.value}
                  value={option?.value}
                >
                  {option?.label}
                </StyledNewSelect.Option>
              );
            })}
          </StyledNewSelect>
        </StyledModalContent>
        <Button
          isMobile={isMobile}
          nextButtonDisabled={page_content?.submit_button?.disable}
          hideNextButtonIcon
          continueButtonText={page_content?.submit_button?.label}
          backButtonText={page_content?.back_button?.label}
          onBackClick={() => modalHandler()}
          onNextClick={() => {
            submitForm();
          }}
        />
      </StyledModal>
    )
  );
};

SocialSelectModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  modalHandler: PropTypes.func.isRequired,
};

export default SocialSelectModal;
